import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, CheckCircle2, ArrowRight, Package, Users, DollarSign, ShoppingCart, FolderKanban, Brain, UserPlus, Settings2, Rocket, Boxes, Bell, ScanLine, CalendarClock, FileCheck2, Clock, TrendingUp, ShieldCheck, Smartphone, Headphones, Sparkles, Quote } from 'lucide-react';
import { Badge } from '../components/Badge';
import { DashboardMockup } from '../components/DashboardMockup';
export function Home() {
  return <div className="w-full" data-id="element-367">
      {/* HERO */}
      <section className="relative pt-16 pb-28 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-indigo-50/40" data-id="element-368">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-369">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-370">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-371">
              <Badge icon={<Star className="w-3.5 h-3.5" data-id="element-373" />} text="AKIOS BOS — FLAGSHIP PRODUCT" data-id="element-372" />
              <h1 className="text-5xl sm:text-6xl lg:text-[4.5rem] font-black leading-[1.05] tracking-tight mb-6" data-id="element-374">
                Run Your Entire{' '}
                <span className="text-brand-blue" data-id="element-375">Business</span> From One{' '}
                <span className="text-brand-purple" data-id="element-376">Intelligent</span> Platform
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl" data-id="element-377">
                Akios BOS brings together inventory, CRM, finance, sales,
                projects, and AI-powered insights into one seamless business
                operating system built for modern growing businesses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-378">
                <Link to="/contact" className="bg-brand-blue hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 group" data-id="element-379">
                  Request a Demo{' '}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-380" />
                </Link>
                <Link to="/contact" className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center shadow-sm" data-id="element-381">
                  Start Free Trial
                </Link>
              </div>
              <p className="text-sm text-slate-500 font-medium flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-382">
                <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-383" />
                No credit card required
                <span className="text-slate-300" data-id="element-384">•</span>
                Setup in days, not months
              </p>
            </motion.div>

            <div className="lg:pl-8" data-id="element-385">
              <DashboardMockup data-id="element-386" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES OVERVIEW */}
      <section id="features" className="py-24 bg-white scroll-mt-24" data-id="element-387">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-388">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-389">
            <Badge icon={<Sparkles className="w-3.5 h-3.5" data-id="element-391" />} text="MODULES" data-id="element-390" />
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" data-id="element-392">
              Everything Your Business Needs in{' '}
              <span className="text-brand-blue" data-id="element-393">One Place</span>
            </h2>
            <p className="text-lg text-slate-600" data-id="element-394">
              Six powerful modules working together as one connected system. No
              more switching between tools, spreadsheets, or disconnected
              workflows.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-395">
            {[{
            icon: Package,
            title: 'Inventory Management',
            desc: 'Track stock in real time across warehouses, automate reorders, scan barcodes instantly, and stay ahead of inventory shortages before they impact operations.',
            color: 'text-blue-600',
            bg: 'bg-blue-50'
          }, {
            icon: Users,
            title: 'Customer Relationship',
            desc: 'Build stronger customer relationships with a complete CRM that tracks conversations, pipelines, follow-ups, and every interaction in one place.',
            color: 'text-purple-600',
            bg: 'bg-purple-50'
          }, {
            icon: DollarSign,
            title: 'Financial Management',
            desc: 'Simplify invoicing, expenses, GST compliance, and cash flow tracking with finance tools designed for fast-moving businesses.',
            color: 'text-green-600',
            bg: 'bg-green-50'
          }, {
            icon: ShoppingCart,
            title: 'Sales & Orders',
            desc: 'Manage orders across every sales channel with complete visibility from quotation to delivery, all updated in real time.',
            color: 'text-amber-600',
            bg: 'bg-amber-50'
          }, {
            icon: FolderKanban,
            title: 'Project Management',
            desc: 'Plan smarter with Kanban boards, timelines, milestones, and team collaboration tools that keep every project moving forward.',
            color: 'text-pink-600',
            bg: 'bg-pink-50'
          }, {
            icon: Brain,
            title: 'Business Intelligence',
            desc: 'Turn data into decisions with AI-powered dashboards that surface trends, opportunities, forecasts, and actionable insights automatically.',
            color: 'text-indigo-600',
            bg: 'bg-indigo-50'
          }].map((feature, i) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.06
          }} className="p-6 rounded-2xl border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all bg-white" data-id="element-396">
                <div className={`w-12 h-12 rounded-xl ${feature.bg} ${feature.color} flex items-center justify-center mb-5`} data-id="element-397">
                  <feature.icon className="w-6 h-6" data-id="element-398" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-399">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-400">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-slate-50" data-id="element-401">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-402">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-403">
            <Badge icon={<Rocket className="w-3.5 h-3.5" data-id="element-405" />} text="HOW IT WORKS" data-id="element-404" />
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" data-id="element-406">
              Get Started in{' '}
              <span className="text-brand-purple" data-id="element-407">3 Simple Steps</span>
            </h2>
            <p className="text-lg text-slate-600" data-id="element-408">
              No complicated onboarding. No expensive implementation cycles.
              Akios BOS is designed to get your business up and running fast.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 relative" data-id="element-409">
            {[{
            icon: UserPlus,
            num: '01',
            title: 'Sign Up & Connect',
            desc: 'Create your workspace in minutes and import your existing business data with simple one-click integrations.'
          }, {
            icon: Settings2,
            num: '02',
            title: 'Configure & Customize',
            desc: 'Set up modules, workflows, permissions, and dashboards tailored to how your business actually operates.'
          }, {
            icon: Rocket,
            num: '03',
            title: 'Launch & Grow',
            desc: 'Go live confidently with onboarding support, team training, and guided setup from our success team.'
          }].map((step, i) => <motion.div key={step.num} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm relative" data-id="element-410">
                <div className="text-6xl font-black text-brand-blue/15 absolute top-4 right-6" data-id="element-411">
                  {step.num}
                </div>
                <div className="w-14 h-14 rounded-2xl bg-blue-50 text-brand-blue flex items-center justify-center mb-6 relative" data-id="element-412">
                  <step.icon className="w-7 h-7" data-id="element-413" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-414">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-415">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* INVENTORY MODULE SPOTLIGHT */}
      <section className="py-24 bg-white" data-id="element-416">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-417">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-418">
            <Badge icon={<Boxes className="w-3.5 h-3.5" data-id="element-420" />} text="MODULE SPOTLIGHT" data-id="element-419" />
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" data-id="element-421">
              Explore Every Module in{' '}
              <span className="text-brand-blue" data-id="element-422">Detail</span>
            </h2>
            <p className="text-lg text-slate-600" data-id="element-423">
              Each Akios BOS module is built to work independently — and even
              better together.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center bg-gradient-to-br from-slate-50 to-indigo-50/40 rounded-3xl p-8 md:p-12 border border-slate-200" data-id="element-424">
            <div data-id="element-425">
              <Badge icon={<Package className="w-3.5 h-3.5" data-id="element-427" />} text="INVENTORY MODULE" data-id="element-426" />
              <h3 className="text-3xl md:text-4xl font-black mb-4 tracking-tight" data-id="element-428">
                Smart Inventory Management for Modern Businesses
              </h3>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-429">
                Stay in control of stock, suppliers, warehouses, and purchase
                orders with intelligent inventory tools that reduce errors and
                improve efficiency.
              </p>
              <div className="space-y-4 mb-10" data-id="element-430">
                {[{
                icon: Boxes,
                text: 'Multi-location inventory tracking'
              }, {
                icon: Bell,
                text: 'Automated low-stock alerts'
              }, {
                icon: ScanLine,
                text: 'Barcode & QR code support'
              }, {
                icon: CalendarClock,
                text: 'Expiry & batch management'
              }, {
                icon: FileCheck2,
                text: 'Purchase order automation'
              }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-431">
                    <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 shadow-sm flex items-center justify-center text-brand-blue shrink-0" data-id="element-432">
                      <f.icon className="w-4 h-4" data-id="element-433" />
                    </div>
                    <span className="text-slate-700 font-medium" data-id="element-434">{f.text}</span>
                  </div>)}
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-blue hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 group" data-id="element-435">
                Request a Demo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-436" />
              </Link>
            </div>

            {/* Visual mock */}
            <div className="relative" data-id="element-437">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6" data-id="element-438">
                <div className="flex items-center justify-between mb-6" data-id="element-439">
                  <div data-id="element-440">
                    <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" data-id="element-441">
                      Stock Overview
                    </div>
                    <div className="text-2xl font-black text-slate-900" data-id="element-442">
                      1,284 SKUs
                    </div>
                  </div>
                  <div className="px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-bold" data-id="element-443">
                    +12% this month
                  </div>
                </div>
                <div className="space-y-3" data-id="element-444">
                  {[{
                  name: 'Widget Pro X1',
                  stock: 245,
                  status: 'In Stock',
                  color: 'green'
                }, {
                  name: 'Module Set A',
                  stock: 18,
                  status: 'Low Stock',
                  color: 'amber'
                }, {
                  name: 'Premium Kit',
                  stock: 102,
                  status: 'In Stock',
                  color: 'green'
                }, {
                  name: 'Quick Connect',
                  stock: 0,
                  status: 'Out of Stock',
                  color: 'red'
                }].map(item => <div key={item.name} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100" data-id="element-445">
                      <div className="flex items-center gap-3" data-id="element-446">
                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center" data-id="element-447">
                          <Package className="w-4 h-4 text-slate-500" data-id="element-448" />
                        </div>
                        <div data-id="element-449">
                          <div className="font-bold text-sm text-slate-900" data-id="element-450">
                            {item.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-451">
                            {item.stock} units
                          </div>
                        </div>
                      </div>
                      <div className={`text-xs font-bold px-2.5 py-1 rounded-md ${item.color === 'green' ? 'bg-green-50 text-green-700' : item.color === 'amber' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`} data-id="element-452">
                        {item.status}
                      </div>
                    </div>)}
                </div>
              </div>
              <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg border border-slate-200 p-3 flex items-center gap-2" data-id="element-453">
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-454">
                  <Bell className="w-4 h-4" data-id="element-455" />
                </div>
                <div data-id="element-456">
                  <div className="text-xs font-bold text-slate-900" data-id="element-457">
                    Low stock alert
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-458">
                    2 items below threshold
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE BOS */}
      <section className="py-24 bg-slate-50" data-id="element-459">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-460">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-461">
            <Badge icon={<Sparkles className="w-3.5 h-3.5" data-id="element-463" />} text="WHY AKIOS BOS" data-id="element-462" />
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" data-id="element-464">
              Why Businesses Choose{' '}
              <span className="text-brand-blue" data-id="element-465">Akios BOS</span>
            </h2>
            <p className="text-lg text-slate-600" data-id="element-466">
              Real operational impact from day one.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-467">
            {[{
            icon: Clock,
            title: 'Save 8+ Hours Every Week',
            desc: 'Automate repetitive work and reduce manual processes across teams.',
            color: 'text-blue-600',
            bg: 'bg-blue-50'
          }, {
            icon: TrendingUp,
            title: 'Increase Revenue by 23%',
            desc: 'Businesses using Akios BOS report faster operations and better sales visibility.',
            color: 'text-green-600',
            bg: 'bg-green-50'
          }, {
            icon: ShieldCheck,
            title: 'Enterprise-Grade Security',
            desc: 'Your business data is protected with advanced encryption and secure cloud infrastructure.',
            color: 'text-purple-600',
            bg: 'bg-purple-50'
          }, {
            icon: Smartphone,
            title: 'Access Anywhere',
            desc: 'Manage your business from web, tablet, or mobile — anytime, anywhere.',
            color: 'text-amber-600',
            bg: 'bg-amber-50'
          }, {
            icon: Headphones,
            title: 'Dedicated Support',
            desc: 'Get onboarding guidance, training, and expert support whenever you need it.',
            color: 'text-pink-600',
            bg: 'bg-pink-50'
          }, {
            icon: Rocket,
            title: 'Built to Scale',
            desc: "Whether you're a 5-person startup or a growing enterprise, Akios BOS grows with your business.",
            color: 'text-indigo-600',
            bg: 'bg-indigo-50'
          }].map((b, i) => <motion.div key={b.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.06
          }} className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-lg hover:-translate-y-1 transition-all" data-id="element-468">
                <div className={`w-12 h-12 rounded-xl ${b.bg} ${b.color} flex items-center justify-center mb-5`} data-id="element-469">
                  <b.icon className="w-6 h-6" data-id="element-470" />
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-471">{b.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-472">
                  {b.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 bg-white scroll-mt-24" data-id="element-473">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-474">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-475">
            <Badge icon={<DollarSign className="w-3.5 h-3.5" data-id="element-477" />} text="PRICING" data-id="element-476" />
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" data-id="element-478">
              Simple Pricing That{' '}
              <span className="text-brand-purple" data-id="element-479">Grows With You</span>
            </h2>
            <p className="text-lg text-slate-600" data-id="element-480">
              Transparent plans with no hidden fees. Start small and scale when
              you're ready.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-481">
            {[{
            name: 'Starter',
            tagline: 'Perfect for small businesses getting organized.',
            price: '₹2,999',
            period: '/month',
            features: ['Up to 5 users', 'Inventory Management', 'Basic CRM', 'Billing & Invoicing', 'Email Support'],
            cta: 'Start Free Trial',
            featured: false
          }, {
            name: 'Growth',
            tagline: 'Built for fast-growing teams.',
            price: '₹7,999',
            period: '/month',
            features: ['Up to 25 users', 'Advanced CRM & Sales Pipeline', 'Financial Reports', 'Project Management', 'Priority Support'],
            cta: 'Request Demo',
            featured: true
          }, {
            name: 'Enterprise',
            tagline: 'Custom solutions for large organizations.',
            price: 'Custom',
            period: 'Pricing',
            features: ['Unlimited users', 'AI Business Intelligence', 'Custom Integrations', 'Dedicated Success Manager', 'SLA-backed support'],
            cta: 'Contact Sales',
            featured: false
          }].map((plan, i) => <motion.div key={plan.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative rounded-2xl p-8 border transition-all ${plan.featured ? 'bg-slate-900 text-white border-slate-900 shadow-2xl scale-100 md:scale-105' : 'bg-white border-slate-200 hover:shadow-lg'}`} data-id="element-482">
                {plan.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-blue text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full" data-id="element-483">
                    Most Popular
                  </div>}
                <h3 className={`text-2xl font-black mb-2 ${plan.featured ? 'text-white' : 'text-slate-900'}`} data-id="element-484">
                  {plan.name}
                </h3>
                <p className={`text-sm mb-6 ${plan.featured ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-485">
                  {plan.tagline}
                </p>
                <div className="mb-8" data-id="element-486">
                  <span className={`text-5xl font-black ${plan.featured ? 'text-white' : 'text-slate-900'}`} data-id="element-487">
                    {plan.price}
                  </span>
                  <span className={`text-sm ml-1 ${plan.featured ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-488">
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-3 mb-8" data-id="element-489">
                  {plan.features.map(f => <li key={f} className="flex items-start gap-2" data-id="element-490">
                      <CheckCircle2 className={`w-5 h-5 shrink-0 mt-0.5 ${plan.featured ? 'text-blue-400' : 'text-brand-blue'}`} data-id="element-491" />
                      <span className={`text-sm ${plan.featured ? 'text-slate-200' : 'text-slate-700'}`} data-id="element-492">
                        {f}
                      </span>
                    </li>)}
                </ul>
                <Link to="/contact" className={`block text-center w-full py-3 rounded-xl font-bold transition-all ${plan.featured ? 'bg-white text-slate-900 hover:bg-slate-100' : 'bg-brand-blue text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'}`} data-id="element-493">
                  {plan.cta}
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-slate-50" data-id="element-494">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-495">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-496">
            <Badge icon={<Star className="w-3.5 h-3.5" data-id="element-498" />} text="TESTIMONIALS" data-id="element-497" />
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" data-id="element-499">
              Trusted by{' '}
              <span className="text-brand-blue" data-id="element-500">Growing Businesses</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-501">
            {[{
            quote: 'Akios BOS completely transformed how we manage operations. Everything from inventory to billing now works seamlessly together.',
            name: 'Priya Nair',
            role: 'RetailPro Hyderabad',
            initials: 'PN',
            bg: 'bg-purple-500'
          }, {
            quote: 'The financial dashboards alone changed the way we make decisions. We finally have real-time visibility into our business.',
            name: 'Rahul Mehta',
            role: 'AgroLink Industries',
            initials: 'RM',
            bg: 'bg-blue-500'
          }, {
            quote: 'We were fully onboarded in just three days. The implementation process was smooth, simple, and surprisingly fast.',
            name: 'Deepa Krishnan',
            role: 'MediCare Logistics',
            initials: 'DK',
            bg: 'bg-green-500'
          }].map((t, i) => <motion.div key={t.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm relative" data-id="element-502">
                <Quote className="w-10 h-10 text-blue-100 absolute top-6 right-6" data-id="element-503" />
                <div className="flex gap-1 mb-4" data-id="element-504">
                  {[1, 2, 3, 4, 5].map(n => <Star key={n} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-505" />)}
                </div>
                <p className="text-slate-700 leading-relaxed mb-6 relative" data-id="element-506">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3 pt-6 border-t border-slate-100" data-id="element-507">
                  <div className={`w-11 h-11 rounded-full ${t.bg} text-white font-bold flex items-center justify-center`} data-id="element-508">
                    {t.initials}
                  </div>
                  <div data-id="element-509">
                    <div className="font-bold text-slate-900" data-id="element-510">{t.name}</div>
                    <div className="text-sm text-slate-500" data-id="element-511">{t.role}</div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-512">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 to-brand-purple/20" data-id="element-513" />
        <div className="max-w-4xl mx-auto px-4 relative z-10 text-center" data-id="element-514">
          <h2 className="text-4xl md:text-6xl font-black mb-6 text-white leading-tight tracking-tight" data-id="element-515">
            Run Your Business Smarter With{' '}
            <span className="text-blue-400" data-id="element-516">Akios BOS</span>
          </h2>
          <p className="text-xl text-slate-300 mb-10" data-id="element-517">
            One platform. Complete visibility. Smarter decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-518">
            <Link to="/contact" className="bg-brand-blue hover:bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-600/20 inline-flex items-center justify-center gap-2 group" data-id="element-519">
              Request a Demo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-520" />
            </Link>
            <Link to="/contact" className="bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center" data-id="element-521">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>;
}